export const FrequencyUnits = {
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
} as const

export type FrequencyUnit = (typeof FrequencyUnits)[keyof typeof FrequencyUnits]

export interface ScheduledQueryFrequency {
  unit: FrequencyUnit
  // cron day-of-week, 0 = Sunday … 6 = Saturday (weekly)
  dayOfWeek: number
  // 1-31 (monthly and yearly)
  dayOfMonth: number
  // 1-12 (yearly)
  month: number
}

export const DEFAULT_FREQUENCY: ScheduledQueryFrequency = {
  unit: FrequencyUnits.WEEK,
  dayOfWeek: 1,
  dayOfMonth: 1,
  month: 1,
}

export const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  0: 'Sunday',
}

export const MONTH_LABELS: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
}

// Maximum day-of-month per month that exists every year (February capped at 28 so yearly
// schedules don't silently become leap-year-only)
export const DAYS_IN_MONTH: Record<number, number> = {
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
}

export const ordinal = (day: number): string => {
  const remainder10 = day % 10
  const remainder100 = day % 100
  if (remainder10 === 1 && remainder100 !== 11) return `${day}st`
  if (remainder10 === 2 && remainder100 !== 12) return `${day}nd`
  if (remainder10 === 3 && remainder100 !== 13) return `${day}rd`
  return `${day}th`
}

// Cron hour for runs, evaluated by the scheduler in UTC. 8:00 UTC is past midnight Pacific but
// hours before East Coast mornings, so the run date matches the US-local date and results are
// ready at the start of the day across the continental US.
const RUN_HOUR = 8

// Spread runs across the hour rather than firing every schedule on the minute, so a wave of
// schedules sharing a day/time doesn't hit the scheduler all at once.
const randomRunMinute = (): number => Math.floor(Math.random() * 60)

export const frequencyToCronExpressions = (frequency: ScheduledQueryFrequency, minute: number = randomRunMinute()): string[] => {
  switch (frequency.unit) {
    case FrequencyUnits.MONTH:
      return [`${minute} ${RUN_HOUR} ${frequency.dayOfMonth} * *`]
    case FrequencyUnits.YEAR:
      return [`${minute} ${RUN_HOUR} ${frequency.dayOfMonth} ${frequency.month} *`]
    case FrequencyUnits.WEEK:
    default:
      return [`${minute} ${RUN_HOUR} * * ${frequency.dayOfWeek}`]
  }
}

// Only the exact shapes produced by frequencyToCronExpressions (any minute/hour, so schedules
// created with an older RUN_HOUR or a different run minute still parse); anything else falls back
// to the default frequency
const WEEKLY_CRON = /^\d{1,2} \d{1,2} \* \* (\d)$/
const MONTHLY_CRON = /^\d{1,2} \d{1,2} (\d{1,2}) \* \*$/
const YEARLY_CRON = /^\d{1,2} \d{1,2} (\d{1,2}) (\d{1,2}) \*$/

export const cronExpressionsToFrequency = (cronExpressions: string[]): ScheduledQueryFrequency => {
  const expression = cronExpressions[0] ?? ''
  const weekly = expression.match(WEEKLY_CRON)
  if (weekly) {
    // cron allows both 0 and 7 for Sunday
    const dayOfWeek = Number(weekly[1]) === 7 ? 0 : Number(weekly[1])
    if (dayOfWeek >= 0 && dayOfWeek <= 6) {
      return { ...DEFAULT_FREQUENCY, unit: FrequencyUnits.WEEK, dayOfWeek }
    }
    return DEFAULT_FREQUENCY
  }
  const monthly = expression.match(MONTHLY_CRON)
  if (monthly) {
    const dayOfMonth = Number(monthly[1])
    if (dayOfMonth >= 1 && dayOfMonth <= 31) {
      return { ...DEFAULT_FREQUENCY, unit: FrequencyUnits.MONTH, dayOfMonth }
    }
    return DEFAULT_FREQUENCY
  }
  const yearly = expression.match(YEARLY_CRON)
  if (yearly) {
    const dayOfMonth = Number(yearly[1])
    const month = Number(yearly[2])
    if (month >= 1 && month <= 12 && dayOfMonth >= 1 && dayOfMonth <= DAYS_IN_MONTH[month]) {
      return { ...DEFAULT_FREQUENCY, unit: FrequencyUnits.YEAR, dayOfMonth, month }
    }
    return DEFAULT_FREQUENCY
  }
  return DEFAULT_FREQUENCY
}

// The longest supported wait is a yearly schedule that just passed (≤ 366 days);
// the generous cap only bounds the loop for inputs that slipped past validation
const MAX_NEXT_RUN_SEARCH_DAYS = 366 * 4 + 1

export const getNextRun = (frequency: ScheduledQueryFrequency, from: Date = new Date(), hour: number = RUN_HOUR, minute: number = 0): Date | null => {
  const candidate = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate(), hour, minute, 0, 0))
  const advanceDay = () => candidate.setUTCDate(candidate.getUTCDate() + 1)
  if (candidate <= from) {
    advanceDay()
  }
  const matches = (): boolean => {
    switch (frequency.unit) {
      case FrequencyUnits.WEEK:
        return candidate.getUTCDay() === frequency.dayOfWeek
      case FrequencyUnits.MONTH:
        return candidate.getUTCDate() === frequency.dayOfMonth
      case FrequencyUnits.YEAR:
        return candidate.getUTCDate() === frequency.dayOfMonth && candidate.getUTCMonth() + 1 === frequency.month
    }
  }
  for (let i = 0; i < MAX_NEXT_RUN_SEARCH_DAYS; i++) {
    if (matches()) {
      return candidate
    }
    advanceDay()
  }
  return null
}

// Next run using the cron's actual minute + hour (the frequency drops them), so the date doesn't skip
// "today" when now is past RUN_HOUR:00 but still before the randomized run minute.
export const getNextRunForCron = (cronExpressions: string[], from: Date = new Date()): Date | null => {
  const frequency = cronExpressionsToFrequency(cronExpressions)
  const [minute, hour] = (cronExpressions[0] ?? '').split(' ').map(Number)
  return getNextRun(frequency, from, Number.isFinite(hour) ? hour : RUN_HOUR, Number.isFinite(minute) ? minute : 0)
}

// The cron's nominal date in UTC, so the banner matches the scheduler regardless of the viewer's timezone
export const formatNextRun = (date: Date): string => {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
}
